import referencesError from "ProgramAdmin/Components/referencesError.vue";
import { KEYS, NAMESPACE } from "./i18n/communicationSavingHelper.i18n.js";
import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
import tagErrorsDialog from "TemplateTags/tagErrorsDialog.vue";

// mirrors SaveCommunicationTemplateResultStatus in EditCommunicationTemplateAjax.cs
export const SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS = {
    Success: 0,
    BlockingReferences: 1,
    BrokenTags: 2,
    BlockingFormLinks: 3,
    InvalidRecipient: 4,
    MissingSubject: 5,
    MissingContent: 6
};

export const COMMUNICATION_DIALOG_RESULT = {
    okResult: "OK",
    cancelResult: "Cancel"
};

function i18nMessageText($app, namespace, messageKey, values = {}) {
    return $app.res({
        namespace: namespace,
        key: messageKey,
        values: values
    });
}

export async function _handleSaveTemplateError($app, saveTemplateResult, isAnnouncement) {
    switch (saveTemplateResult.Status) {
        case SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS.BlockingReferences:
            _showReferencesErrorDialog($app, saveTemplateResult.ReferencesError);
            return false;
        case SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS.BrokenTags:
            _showTagErrorsDialog($app, saveTemplateResult.CreateTagsResultModel, isAnnouncement);
            return false;
        case SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS.BlockingFormLinks:
            return _showFormLinksErrorDialog($app, saveTemplateResult.ReferencesError);
        case SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS.InvalidRecipient:
            _showInvalidRecipientErrorDialog($app, isAnnouncement);
            return false;
        case SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS.MissingContent:
        case SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS.MissingSubject:
            _showMissingMessageInfoErrorDialog($app, saveTemplateResult.Status);
            return false;
        default:
            throw `Unhandled save template result status ${saveTemplateResult.Status}`;
    }
}

function _showReferencesErrorDialog($app, referencesErrorModel) {
    $app.showDialog({
        title: i18nMessageText($app, NAMESPACE, KEYS.templateErrorDialogTitle),
        customComponent: referencesError,
        customComponentOptions: {
            referencesErrorModel,
            messagesTitle: i18nMessageText($app, NAMESPACE, KEYS.referencesErrorDialogMessagesLabel)
        },
        buttons: [
            {
                text: i18nMessageText($app, COMMON_NAMESPACE, COMMON_KEYS.okText),
                primary: true,
                result: "ok"
            }
        ]
    });
}

async function _showTagErrorsDialog($app, createTagsResultModel, isAnnouncement) {
    const dismissResult = "DISMISS";

    await $app.showDialog({
        title: i18nMessageText($app, NAMESPACE, KEYS.templateErrorDialogTitle),
        testRole: "tag-error-dialog",
        customComponent: tagErrorsDialog,
        customComponentOptions: {
            createTagsResultModel: createTagsResultModel,
            infoMessage: isAnnouncement
                ? i18nMessageText($app, NAMESPACE, KEYS.announcementTagErrorsDialogInfoMsg)
                : i18nMessageText($app, NAMESPACE, KEYS.communicationTagErrorsDialogInfoMsg)
        },
        buttons: [
            {
                id: "TagChangesOkBtn",
                text: i18nMessageText($app, COMMON_NAMESPACE, COMMON_KEYS.okText),
                primary: true,
                result: dismissResult
            }
        ]
    });
}

async function _showFormLinksErrorDialog($app, referencesErrorModel) {
    const continueResult = "continue";
    const cancelResult = "cancel";

    const result = await $app.showDialog({
        title: i18nMessageText($app, NAMESPACE, KEYS.templateErrorDialogTitle),
        customComponent: referencesError,
        customComponentOptions: {
            referencesErrorModel,
            messagesTitle: i18nMessageText($app, NAMESPACE, KEYS.formStatusMappingConflictDialogTitle)
        },
        buttons: [
            {
                text: i18nMessageText($app, COMMON_NAMESPACE, COMMON_KEYS.cancelText),
                result: cancelResult
            },
            {
                text: i18nMessageText($app, NAMESPACE, KEYS.formStatusMappingConflictDialogContinueBtnLabel),
                primary: true,
                result: continueResult
            }
        ]
    });
    return result == continueResult;
}

async function _showInvalidRecipientErrorDialog($app, isAnnouncement) {
    const dismissResult = "DISMISS";

    const result = await $app.showDialog({
        title: i18nMessageText($app, NAMESPACE, KEYS.templateErrorDialogTitle),
        message: isAnnouncement
            ? i18nMessageText($app, NAMESPACE, KEYS.invalidEmailAddressesForAnnouncementsMessage)
            : i18nMessageText($app, NAMESPACE, KEYS.invalidEmailAddressesForCommunicationMessage),
        buttons: [
            {
                text: i18nMessageText($app, COMMON_NAMESPACE, COMMON_KEYS.okText),
                primary: true,
                result: dismissResult
            }
        ]
    });
    return result == dismissResult;
}

async function _showMissingMessageInfoErrorDialog($app, status) {
    const dismissResult = "DISMISS";

    const result = await $app.showDialog({
        title: i18nMessageText($app, NAMESPACE, KEYS.templateErrorDialogTitle),
        message:
            status === SAVE_COMMUNICATION_TEMPLATE_RESULT_STATUS.MissingContent
                ? i18nMessageText($app, NAMESPACE, KEYS.missingContentMessage)
                : i18nMessageText($app, NAMESPACE, KEYS.missingSubjectMessage),
        buttons: [
            {
                text: i18nMessageText($app, COMMON_NAMESPACE, COMMON_KEYS.okText),
                primary: true,
                result: dismissResult
            }
        ]
    });
    return result == dismissResult;
}

export function getDetailStringForStatuses($app, commTemplate, allStatuses) {
    if (commTemplate.AvailableInAllStatuses) {
        return i18nMessageText($app, NAMESPACE, KEYS.availableToAllStatuses);
    }

    const statusNames = allStatuses
        .filter((s) => commTemplate.AvailableInStatusIds.indexOf(s.PublicId) >= 0)
        .map((s) => s.Name)
        .sort();

    if (statusNames.length > 0) {
        if (statusNames.length == 1) {
            return i18nMessageText($app, NAMESPACE, KEYS.availableToOneStatus, { statusName: statusNames[0] });
        } else {
            let count = 0;
            let statusString = "";
            const conjunction = i18nMessageText($app, NAMESPACE, KEYS.andText);

            statusNames.forEach((status) => {
                statusString = statusString + status;
                if (count < statusNames.length - 2) {
                    statusString = statusString + ", ";
                } else if (count < statusNames.length - 1) {
                    statusString = statusString + " " + conjunction + " ";
                }
                count++;
            });

            return i18nMessageText($app, NAMESPACE, KEYS.availableToSomeStatuses, { statusNames: statusString });
        }
    } else {
        return i18nMessageText($app, NAMESPACE, KEYS.availableToNoStatuses);
    }
}
